import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Alert,
  Grid,
  Divider,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

function ClusterCard({ cluster, index }) {
  const [expanded, setExpanded] = useState(false);

  const getRiskColor = (level) => {
    switch (level) {
      case 'HIGH': return 'error';
      case 'MEDIUM': return 'warning';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  const copyUserIds = () => {
    navigator.clipboard.writeText(cluster.userIds.join('\n'));
    alert(`Copied ${cluster.userIds.length} user IDs`);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              label={`Cluster #${index + 1}`} 
              color="primary" 
              size="small"
            />
            <Chip 
              label={`Risk: ${cluster.riskScore}/100`}
              color={getRiskColor(cluster.riskLevel)}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {cluster.userCount} users • {cluster.transactionCount} transactions • {cluster.spreadSeconds}s spread
            </Typography>
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: '0.3s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Box display="flex" gap={1} mb={2}>
          {cluster.bannedCount > 0 && (
            <Chip 
              label={`${cluster.bannedCount} Banned`} 
              color="error" 
              size="small"
            />
          )}
          {cluster.cheaterCount > 0 && (
            <Chip 
              label={`${cluster.cheaterCount} Cheater`} 
              color="warning" 
              size="small"
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Time: {new Date(cluster.windowStart).toLocaleString()} 
        </Typography>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />
          
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Users in this cluster:</Typography>
              <Button 
                size="small" 
                startIcon={<CopyIcon />}
                onClick={copyUserIds}
              >
                Copy IDs
              </Button>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              {cluster.users?.map(user => (
                <Box 
                  key={user.id} 
                  display="flex" 
                  alignItems="center" 
                  gap={1}
                  sx={{ 
                    p: 1, 
                    bgcolor: 'background.default',
                    borderRadius: 1 
                  }}
                >
                  <Typography 
                    variant="body2" 
                    component="a"
                    href={`/user/${user.id}/details`}
                    target="_blank"
                    sx={{ 
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {user.name || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                  {user.banned && <Chip label="BANNED" color="error" size="small" />}
                  {user.cheater && <Chip label="CHEATER" color="warning" size="small" />}
                </Box>
              ))}
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Transactions: {cluster.transactionIds.length} • Avg Amount: {cluster.avgAmount}
          </Typography>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function MultiAccountDetection() {
  const [analyzing, setAnalyzing] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });
  const [clusters, setClusters] = useState([]);
  const [availableContexts, setAvailableContexts] = useState([]);
  
  const [fetchConfig, setFetchConfig] = useState({
    context: 'DeliveryMissionComplete',
    lookbackDays: 30
  });
  
  const [analysisConfig, setAnalysisConfig] = useState({
    timeWindowSeconds: 5,
    minUsers: 3
  });
  
  const cachedTransactions = useRef([]);
  const cacheMetadata = useRef({
    context: null,
    lookbackDays: null,
    fetchedAt: null,
    totalCount: 0
  });

  useEffect(() => {
    loadAvailableContexts();
  }, []);

  const loadAvailableContexts = async () => {
    try {
      const env = localStorage.getItem('selectedEnvironment') || 'production';
      const apiPrefix = env === 'development' ? '/api-dev' : '/api';
      const response = await fetch(`${apiPrefix}/multi-account-detection/contexts?lookbackDays=30`);
      const { contexts } = await response.json();
      setAvailableContexts(contexts);
      
      if (contexts.length > 0 && !fetchConfig.context) {
        setFetchConfig(prev => ({ ...prev, context: contexts[0].context }));
      }
    } catch (error) {
      console.error('Failed to load contexts:', error);
    }
  };

  const fetchTransactions = async () => {
    setFetching(true);
    cachedTransactions.current = [];
    setClusters([]);
    
    try {
      // Determine API prefix based on environment
      const env = localStorage.getItem('selectedEnvironment') || 'production';
      const apiPrefix = env === 'development' ? '/api-dev' : '/api';
      
      let allTransactions = [];
      let offset = 0;
      const limit = 50000;
      let hasMore = true;
      
      setProgress({ current: 0, total: 100, status: 'Fetching transactions from database...' });
      
      while (hasMore) {
        const response = await fetch(
          `${apiPrefix}/multi-account-detection/transactions?context=${encodeURIComponent(fetchConfig.context)}&lookbackDays=${fetchConfig.lookbackDays}&offset=${offset}&limit=${limit}`
        );
        const { transactions, total, hasMore: more } = await response.json();
        
        allTransactions = [...allTransactions, ...transactions];
        offset += transactions.length;
        hasMore = more;
        
        const progressPercent = Math.min((offset / total) * 100, 100);
        setProgress({ 
          current: progressPercent, 
          total: 100, 
          status: `Fetched ${offset.toLocaleString()} / ${total.toLocaleString()} transactions...` 
        });
        
        await new Promise(resolve => setTimeout(resolve, 10));
      }
      
      cachedTransactions.current = allTransactions;
      cacheMetadata.current = {
        context: fetchConfig.context,
        lookbackDays: fetchConfig.lookbackDays,
        fetchedAt: new Date(),
        totalCount: allTransactions.length
      };
      
      setProgress({ 
        current: 100, 
        total: 100, 
        status: `✅ Cached ${allTransactions.length.toLocaleString()} transactions` 
      });
      
      await analyzeTransactions();
    } catch (error) {
      console.error('Error fetching transactions:', error);
      alert('Failed to fetch transactions: ' + error.message);
    } finally {
      setFetching(false);
    }
  };

  const analyzeTransactions = async () => {
    if (cachedTransactions.current.length === 0) {
      alert('No cached data. Fetch data first.');
      return;
    }
    
    setAnalyzing(true);
    setClusters([]);
    
    try {
      setProgress({ current: 0, total: 100, status: 'Analyzing transaction patterns...' });
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const foundClusters = findClusters(
        cachedTransactions.current,
        analysisConfig.timeWindowSeconds * 1000,
        analysisConfig.minUsers
      );
      
      setProgress({ current: 50, total: 100, status: `Found ${foundClusters.length} clusters, enriching with user data...` });
      
      const uniqueUserIds = [...new Set(foundClusters.flatMap(c => c.userIds))];
      
      const env = localStorage.getItem('selectedEnvironment') || 'production';
      const apiPrefix = env === 'development' ? '/api-dev' : '/api';
      const response = await fetch(`${apiPrefix}/multi-account-detection/user-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: uniqueUserIds })
      });
      
      const { users } = await response.json();
      const enrichedClusters = enrichClustersWithUsers(foundClusters, users);
      
      setClusters(enrichedClusters);
      setProgress({ 
        current: 100, 
        total: 100, 
        status: `✅ Analysis complete: ${enrichedClusters.length} suspicious clusters found` 
      });
    } catch (error) {
      console.error('Error analyzing:', error);
      alert('Analysis failed: ' + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const findClusters = (transactions, windowMs, minUsers) => {
    const sorted = [...transactions].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    
    const clusters = [];
    
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && new Date(sorted[i].createdAt).getTime() - new Date(sorted[i-1].createdAt).getTime() < 100) {
        continue;
      }
      
      const windowStart = new Date(sorted[i].createdAt).getTime();
      const windowEnd = windowStart + windowMs;
      
      const clusterTransactions = [];
      const userIds = new Set();
      
      for (let j = i; j < sorted.length; j++) {
        const txTime = new Date(sorted[j].createdAt).getTime();
        
        if (txTime <= windowEnd) {
          clusterTransactions.push(sorted[j]);
          userIds.add(sorted[j].userId);
        } else {
          break;
        }
      }
      
      if (userIds.size >= minUsers) {
        const lastTxTime = new Date(clusterTransactions[clusterTransactions.length - 1].createdAt).getTime();
        
        clusters.push({
          id: `cluster-${i}-${windowStart}`,
          windowStart: new Date(windowStart),
          windowEnd: new Date(windowEnd),
          userCount: userIds.size,
          transactionCount: clusterTransactions.length,
          userIds: Array.from(userIds),
          transactionIds: clusterTransactions.map(t => t.id),
          transactions: clusterTransactions,
          spreadSeconds: ((lastTxTime - windowStart) / 1000).toFixed(2),
          avgAmount: (clusterTransactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0) / clusterTransactions.length).toFixed(2)
        });
      }
    }
    
    return deduplicateClusters(clusters);
  };

  const deduplicateClusters = (clusters) => {
    const unique = [];
    const seen = new Set();
    
    clusters.sort((a, b) => b.userCount - a.userCount);
    
    for (const cluster of clusters) {
      const key = cluster.userIds.sort().join(',');
      if (!seen.has(key)) {
        unique.push(cluster);
        seen.add(key);
      }
    }
    
    return unique;
  };

  const enrichClustersWithUsers = (clusters, users) => {
    const userMap = new Map(users.map(u => [u.id, u]));
    
    return clusters.map(cluster => {
      const clusterUsers = cluster.userIds
        .map(id => userMap.get(id))
        .filter(Boolean);
      
      let riskScore = 0;
      riskScore += Math.min(cluster.userCount * 5, 50);
      
      const spread = parseFloat(cluster.spreadSeconds);
      if (spread < 2) riskScore += 30;
      else if (spread < 5) riskScore += 20;
      else if (spread < 10) riskScore += 10;
      
      const txPerUser = cluster.transactionCount / cluster.userCount;
      if (txPerUser > 1) riskScore += 20;
      
      const bannedCount = clusterUsers.filter(u => u.banned).length;
      const cheaterCount = clusterUsers.filter(u => u.cheater).length;
      if (bannedCount > 0) riskScore += bannedCount * 5;
      if (cheaterCount > 0) riskScore += cheaterCount * 5;
      
      return {
        ...cluster,
        users: clusterUsers,
        bannedCount,
        cheaterCount,
        riskScore: Math.min(riskScore, 100),
        riskLevel: riskScore >= 75 ? 'HIGH' : riskScore >= 50 ? 'MEDIUM' : 'LOW'
      };
    }).sort((a, b) => b.riskScore - a.riskScore);
  };

  const isCacheValid = () => {
    return cachedTransactions.current.length > 0 &&
           cacheMetadata.current.context === fetchConfig.context &&
           cacheMetadata.current.lookbackDays === fetchConfig.lookbackDays;
  };

  const generateCSV = (clusters) => {
    const rows = [['Cluster', 'Risk Score', 'Risk Level', 'User Count', 'Transaction Count', 'Spread (s)', 'User IDs', 'Time']];
    
    clusters.forEach((cluster, idx) => {
      rows.push([
        idx + 1,
        cluster.riskScore,
        cluster.riskLevel,
        cluster.userCount,
        cluster.transactionCount,
        cluster.spreadSeconds,
        cluster.userIds.join(';'),
        new Date(cluster.windowStart).toISOString()
      ]);
    });
    
    return rows.map(row => row.join(','));
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Multi-Account Detection
      </Typography>
      
      {/* Step 1: Fetch Data */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Fetch Transaction Data
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Context Type</InputLabel>
              <Select
                value={fetchConfig.context}
                onChange={(e) => setFetchConfig({...fetchConfig, context: e.target.value})}
                disabled={fetching || analyzing}
                label="Context Type"
              >
                {availableContexts.length === 0 ? (
                  <MenuItem value="DeliveryMissionComplete">DeliveryMissionComplete</MenuItem>
                ) : (
                  availableContexts.map(ctx => (
                    <MenuItem key={ctx.context} value={ctx.context}>
                      {ctx.context} ({ctx.count.toLocaleString()} transactions, {ctx.uniqueUsers} users)
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Lookback Period (Days)"
              value={fetchConfig.lookbackDays}
              onChange={(e) => setFetchConfig({...fetchConfig, lookbackDays: parseInt(e.target.value)})}
              inputProps={{ min: 1, max: 365 }}
              disabled={fetching || analyzing}
            />
          </Grid>
        </Grid>
        
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            onClick={fetchTransactions}
            disabled={fetching || analyzing}
            startIcon={<SearchIcon />}
          >
            {fetching ? 'Fetching...' : 'Fetch Data'}
          </Button>
          
          {cacheMetadata.current.fetchedAt && (
            <Box>
              <Typography variant="body2" color="success.main">
                ✅ Cached: {cacheMetadata.current.totalCount.toLocaleString()} transactions 
                ({cacheMetadata.current.context}, {cacheMetadata.current.lookbackDays} days)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fetched {new Date(cacheMetadata.current.fetchedAt).toLocaleTimeString()}
                {!isCacheValid() && (
                  <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                    ⚠️ Config changed - refetch needed
                  </Typography>
                )}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
      
      {/* Step 2: Analysis Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 2: Configure Analysis Parameters
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Time Window (Seconds)"
              value={analysisConfig.timeWindowSeconds}
              onChange={(e) => setAnalysisConfig({...analysisConfig, timeWindowSeconds: parseInt(e.target.value)})}
              inputProps={{ min: 1, max: 300 }}
              disabled={analyzing}
              helperText="How close together? Suggested: 5s (very tight), 10s (tight), 30s (moderate)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Users"
              value={analysisConfig.minUsers}
              onChange={(e) => setAnalysisConfig({...analysisConfig, minUsers: parseInt(e.target.value)})}
              inputProps={{ min: 2, max: 50 }}
              disabled={analyzing}
              helperText="Cluster size threshold. Suggested: 3 (strict), 5 (moderate), 10 (loose)"
            />
          </Grid>
        </Grid>
        
        <Button
          variant="contained"
          color="success"
          onClick={analyzeTransactions}
          disabled={analyzing || cachedTransactions.current.length === 0}
          startIcon={<SearchIcon />}
        >
          {analyzing ? 'Analyzing...' : '🔍 Analyze Cached Data'}
        </Button>
        
        {cachedTransactions.current.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No data cached. Fetch data first.
          </Alert>
        )}
        
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          💡 Tip: You can change parameters and re-analyze instantly without fetching data again!
        </Typography>
      </Paper>
      
      {/* Progress Bar */}
      {(fetching || analyzing) && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2">{progress.status}</Typography>
            <Typography variant="body2" color="text.secondary">
              {progress.total === 100 ? `${progress.current.toFixed(0)}%` : `${progress.current}/${progress.total}`}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress.total === 100 ? progress.current : (progress.current / progress.total) * 100} 
          />
        </Paper>
      )}
      
      {/* Results */}
      {clusters.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">
                Found {clusters.length} Suspicious Cluster{clusters.length !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total suspicious users: {[...new Set(clusters.flatMap(c => c.userIds))].length}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => {
                  const allUserIds = [...new Set(clusters.flatMap(c => c.userIds))];
                  navigator.clipboard.writeText(allUserIds.join('\n'));
                  alert(`Copied ${allUserIds.length} user IDs to clipboard`);
                }}
              >
                Copy All User IDs
              </Button>
              
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  const csv = generateCSV(clusters);
                  downloadCSV(csv, 'multi-account-clusters.csv');
                }}
              >
                Export CSV
              </Button>
            </Stack>
          </Box>
          
          <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
            {clusters.map((cluster, idx) => (
              <ClusterCard key={cluster.id} cluster={cluster} index={idx} />
            ))}
          </Box>
        </Paper>
      )}
      
      {!fetching && !analyzing && clusters.length === 0 && cachedTransactions.current.length > 0 && (
        <Alert severity="success">
          ✅ No suspicious clusters found with current parameters. Try adjusting the time window or minimum users.
        </Alert>
      )}
    </Box>
  );
}
